import type { Dispatch, SetStateAction } from "react"

import type { ReportTemplateFieldDto } from "../../api/reportTemplateService"
import { FIELD_TYPE_OPTIONS, type FieldFormState } from "./editorUtils"
import { Button } from "shared/ui/button"
import { Checkbox } from "shared/ui/checkbox"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"

interface FieldDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingFieldIndex: number | null
  fieldForm: FieldFormState
  setFieldForm: Dispatch<SetStateAction<FieldFormState>>
  onApply: () => void
}

export function FieldDialog({
  open,
  onOpenChange,
  editingFieldIndex,
  fieldForm,
  setFieldForm,
  onApply,
}: FieldDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingFieldIndex === null ? "Add Report Field" : "Edit Report Field"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Field Key *</Label>
            <Input
              value={fieldForm.fieldKey}
              onChange={(event) => setFieldForm((prev) => ({ ...prev, fieldKey: event.target.value }))}
              placeholder="permit_number"
            />
          </div>
          <div className="space-y-2">
            <Label>Field Label *</Label>
            <Input
              value={fieldForm.label}
              onChange={(event) => setFieldForm((prev) => ({ ...prev, label: event.target.value }))}
              placeholder="Permit Number"
            />
          </div>
          <div className="space-y-2">
            <Label>Data Type *</Label>
            <Select
              value={fieldForm.dataType}
              onValueChange={(value: ReportTemplateFieldDto["dataType"]) => setFieldForm((prev) => ({ ...prev, dataType: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Order Index</Label>
            <Input
              type="number"
              min={1}
              value={fieldForm.orderIndex}
              onChange={(event) =>
                setFieldForm((prev) => ({ ...prev, orderIndex: Number.parseInt(event.target.value || "1", 10) || 1 }))
              }
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Source Path</Label>
            <Input
              value={fieldForm.sourcePath}
              onChange={(event) => setFieldForm((prev) => ({ ...prev, sourcePath: event.target.value }))}
              placeholder="permit.permit_number"
            />
          </div>

          <div className="md:col-span-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 rounded-md border p-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="field-visible"
                checked={fieldForm.defaultVisible}
                onCheckedChange={(checked) => setFieldForm((prev) => ({ ...prev, defaultVisible: Boolean(checked) }))}
              />
              <Label htmlFor="field-visible">Default Visible</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="field-filterable"
                checked={fieldForm.isFilterable}
                onCheckedChange={(checked) => setFieldForm((prev) => ({ ...prev, isFilterable: Boolean(checked) }))}
              />
              <Label htmlFor="field-filterable">Filterable</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="field-sortable"
                checked={fieldForm.isSortable}
                onCheckedChange={(checked) => setFieldForm((prev) => ({ ...prev, isSortable: Boolean(checked) }))}
              />
              <Label htmlFor="field-sortable">Sortable</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="field-groupable"
                checked={fieldForm.isGroupable}
                onCheckedChange={(checked) => setFieldForm((prev) => ({ ...prev, isGroupable: Boolean(checked) }))}
              />
              <Label htmlFor="field-groupable">Groupable</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onApply}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
